import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { LoginStart, RegisterStart, SocialLoginStart } from '../../providers/auth/store/auth.actions';
import { AuthService } from '../../providers/auth/auth.service';
import { SocialService } from '../../providers/social/social.service';
import { LoaderService } from '../../providers/loader/loader.service';
import { AlertService } from '../../providers/alert/alert.service';
import { ToastService } from '../../providers/toast/toast.service';
import { UtilService } from '../../providers/util/util.service';
import * as fromApp from '../../app.reducer';

@Component({
	selector: 'app-auth-modal',
	templateUrl: './auth-modal.component.html',
	styleUrls: ['./auth-modal.component.scss'],
})
export class AuthModalComponent implements OnInit, OnDestroy {
	mode: 'signin' | 'register' = 'signin';
	signinForm: FormGroup;
	registerForm: FormGroup;
	pwInputType: 'password' | 'text' = 'password';

	get showGoogleSignIn(): boolean {
		return this.socialService.isGoogleSignInAvailable();
	}

	get showAppleSignIn(): boolean {
		return this.socialService.isAppleSignInAvailable();
	}

	private destroy$ = new Subject<void>();
	private _authSubmitted = false;

	constructor(
		private modalController: ModalController,
		private store: Store<fromApp.AppState>,
		private authService: AuthService,
		private socialService: SocialService,
		private loaderService: LoaderService,
		private alertService: AlertService,
		private toastService: ToastService,
		private utilService: UtilService
	) {}

	ngOnInit(): void {
		this.signinForm = new FormGroup({
			email: new FormControl('', Validators.compose([Validators.required, Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$')])),
			password: new FormControl('', Validators.compose([Validators.required, Validators.minLength(5)])),
		});

		this.registerForm = new FormGroup({
			first: new FormControl('', Validators.required),
			last: new FormControl('', Validators.required),
			email: new FormControl('', Validators.compose([Validators.required, Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$')])),
			password: new FormControl('', Validators.compose([Validators.required, Validators.minLength(5)])),
			passwordConfirm: new FormControl('', Validators.compose([Validators.required, Validators.minLength(5)])),
			allow_marketing: new FormControl(true),
		});

		this.store
			.select('auth')
			.pipe(
				filter(() => this._authSubmitted),
				filter((auth: any) => !!auth.access),
				take(1),
				takeUntil(this.destroy$)
			)
			.subscribe(() => {
				this.modalController.dismiss();
			});
	}

	switchMode(target: 'signin' | 'register'): void {
		this.mode = target;
		this.pwInputType = 'password';
	}

	dismiss(): void {
		this.modalController.dismiss();
	}

	togglePw(): void {
		this.pwInputType = this.pwInputType === 'password' ? 'text' : 'password';
	}

	onSignin(): void {
		if (!this.signinForm.valid) {
			return;
		}
		this._authSubmitted = true;
		this.loaderService.showLoader();
		this.store.dispatch(new LoginStart(this.signinForm.getRawValue()));
	}

	onRegister(): void {
		if (!this.registerForm.valid) {
			return;
		}
		const values = this.registerForm.getRawValue();
		if (values.password !== values.passwordConfirm) {
			this.alertService.show('Oops!', 'Passwords do not match. Please try again.');
			return;
		}
		this._authSubmitted = true;
		this.loaderService.showLoader();
		this.store.dispatch(new RegisterStart({ ...values }));
	}

	signInWithGoogle(): void {
		this.loaderService.showLoader();
		this.store.dispatch(new SocialLoginStart());
		this.socialService
			.signInWithGoogle()
			.then((result) => {
				if (result && result.authTokens) {
					this.authService.token = result.authTokens;
				}
				this.modalController.dismiss();
			})
			.catch((error) => {
				const errorMessage = error?.message || error?.error || 'Failed to continue with Google. Please try again.';
				this.alertService.show('Login Error', errorMessage);
			})
			.finally(() => this.loaderService.hideLoader());
	}

	signInWithApple(): void {
		this.loaderService.showLoader();
		this.store.dispatch(new SocialLoginStart());
		this.socialService
			.signInWithApple()
			.then((result) => {
				if (result && result.authTokens) {
					this.authService.token = result.authTokens;
				}
				this.modalController.dismiss();
			})
			.catch((error) => {
				const errorMessage = error?.message || error?.error || 'Failed to continue with Apple. Please try again.';
				this.alertService.show('Login Error', errorMessage);
			})
			.finally(() => this.loaderService.hideLoader());
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	forgotPassword(): void {
		this.alertService.show(
			'Password Reset',
			'Please enter your email:',
			[
				{
					text: 'Cancel',
					handler: () => true,
				},
				{
					text: 'Send Link',
					handler: (data) => {
						if (this.utilService.isValidEmail(data.email)) {
							this.toastService.show('Password reset link has been emailed');
						} else {
							this.alertService.show('Oops!', 'Invalid email formatting. Please try again.');
						}
					},
				},
			],
			[
				{
					name: 'email',
					placeholder: 'Email',
					type: 'text',
				},
			]
		);
	}
}
